import { MONTH_LABELS } from '@crm/shared';
import { Button, Modal, Spinner } from '@crm/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormError, FormField } from '@/components';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { useGoals, useSaveGoals } from '../hooks';

const goalsFormSchema = z.object({
  customersGoal: z.coerce.number().int().min(0).max(100_000),
  returningGoal: z.coerce.number().int().min(0).max(100_000),
  winRateGoal: z.coerce.number().min(0).max(100),
});

type GoalsFormValues = z.infer<typeof goalsFormSchema>;

export const GOALS_HINT =
  'Cada mes guarda a sua meta. Sem meta gravada, ela nasce da do mes anterior mais 10%. Zero desliga a cobranca no painel.';

export function goalsTitle(goals?: { month: number; year: number }) {
  return goals ? `Metas de ${MONTH_LABELS[goals.month - 1]} de ${goals.year}` : 'Metas deste mes';
}

export function GoalsForm({ onSaved }: { onSaved?: () => void }) {
  const canWrite = useCan('settings', 'WRITE');
  const goals = useGoals();
  const saveGoals = useSaveGoals();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<GoalsFormValues>({
    resolver: zodResolver(goalsFormSchema),
    values: goals.data
      ? {
          customersGoal: goals.data.customersGoal,
          returningGoal: goals.data.returningGoal,
          winRateGoal: goals.data.winRateGoal,
        }
      : undefined,
    defaultValues: { customersGoal: 0, returningGoal: 0, winRateGoal: 0 },
  });

  if (goals.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  if (goals.isError) {
    return <p className="text-sm text-destructive">{toErrorMessage(goals.error)}</p>;
  }

  return (
    <form
      className="space-y-5"
      onSubmit={handleSubmit((values) => saveGoals.mutate(values, { onSuccess: onSaved }))}
      noValidate
    >
      <FormError message={saveGoals.error ? toErrorMessage(saveGoals.error) : null} />

      <FormField
        label="Clientes novos no mes"
        type="number"
        min="0"
        disabled={!canWrite}
        error={errors.customersGoal?.message}
        {...register('customersGoal')}
      />

      <FormField
        label="Clientes de casa que voltam no mes"
        type="number"
        min="0"
        disabled={!canWrite}
        error={errors.returningGoal?.message}
        {...register('returningGoal')}
      />

      <FormField
        label="Win rate: leads do mes que viram horario marcado (%)"
        type="number"
        step="0.01"
        min="0"
        disabled={!canWrite}
        error={errors.winRateGoal?.message}
        {...register('winRateGoal')}
      />

      {canWrite ? (
        <Button type="submit" disabled={saveGoals.isPending}>
          {saveGoals.isPending ? <Spinner /> : null}
          Salvar metas
        </Button>
      ) : null}
    </form>
  );
}

export function GoalsModal({ onClose }: { onClose: () => void }) {
  const goals = useGoals();

  return (
    <Modal open onClose={onClose} title={goalsTitle(goals.data)} subtitle={GOALS_HINT} size="small">
      <GoalsForm onSaved={onClose} />
    </Modal>
  );
}
